/**
 * The upload driver for a pool banner: a `File` in, a `mediaId` ready to hand
 * to `POST /pool/banner/set`, an image waiting on a moderation result, or a
 * retryable failure out.
 *
 * Ported from `apps/app/src/features/messages/useChatPhotoUpload.ts`, which
 * itself stops after `/upload/complete`. This hook does the same: it never
 * calls `/pool/banner/set` itself, because the create flow and the pool
 * Settings card attach the resulting mediaId at different moments (WI-W3,
 * WI-W4 own that call).
 *
 * ISSUE-944, the same rule as the profile photo (`../profile/useAvatarUpload.ts`):
 * `/upload/complete` publishes every upload, whatever the scan concluded, so
 * the ordinary path through this hook ends at `ready` with a mediaId the
 * create flow and the Settings card can use at once. A flagged or unscanned
 * banner is published too and queued for staff, who can take it down later;
 * that takedown flips the media row to `rejected`, which makes the pool's
 * `bannerImageUrl` resolve to null again on the next read with no client
 * change. No automatic check ever lands this hook on `failed`.
 *
 * `failed` therefore means the upload itself did not happen, and it carries
 * the sentence to show, or null for the field's own generic line. An
 * oversized file gets the shared size sentence (ISSUE-839), the one refusal
 * with copy of its own; a wrong type, a dropped PUT and a server fault keep
 * the generic line. None of them is a moderation verdict.
 *
 * `pendingReview` and `heldOnBudget` no longer come back from a fresh upload.
 * The branch stays because a client is not the thing that gets to assume
 * that: an older media-api during a rolling deploy still answers with them,
 * and `/pool/banner/set` refuses any status but `published`, so handing the
 * mediaId over on one would earn a guaranteed VALIDATION_ERROR instead of a
 * sentence a fan can read. `/upload/complete` is synchronous and terminal,
 * so the get-status polling hook is deliberately not imported.
 */
import { FILE_TOO_LARGE_COPY } from "@sportsmet/errors";
import { useUploadComplete, useUploadInit } from "@sportsmet/media-sdk/private";
import { useCallback, useRef, useState } from "react";

import { photoUploadRefusalMessage } from "../../errors/apiErrorBus";

export const MAX_POOL_BANNER_BYTES = 15_000_000;

export const POOL_BANNER_CONTENT_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/heic",
] as const;

export type typePoolBannerContentType =
  (typeof POOL_BANNER_CONTENT_TYPES)[number];

export type typePoolBannerUploadState =
  | { kind: "idle" }
  | { kind: "uploading" }
  | { kind: "ready"; mediaId: string; previewUrl: string }
  | { kind: "pendingReview"; previewUrl: string }
  | { kind: "failed"; message: string | null };

export function usePoolBannerUpload(): {
  state: typePoolBannerUploadState;
  uploadBanner: (file: File) => void;
  reset: () => void;
} {
  const uploadInit = useUploadInit();
  const uploadComplete = useUploadComplete();
  const [state, setState] = useState<typePoolBannerUploadState>({
    kind: "idle",
  });
  // The re-entrancy guard lives on a ref, not on `state`, so the check inside
  // `run` always sees the latest value rather than whatever `state` was when
  // the enclosing callback was created.
  const isUploadingRef = useRef(false);

  const uploadBanner = useCallback(
    (file: File) => {
      // 1. Re-entrancy guard. One upload at a time.
      if (isUploadingRef.current) return;

      async function run(): Promise<void> {
        // 2. Client-side pre-checks, before any network call. These mirror
        // the server's own refusals so an oversized or wrong-type file costs
        // the fan a local check instead of a round trip. The server still
        // refuses independently; this is a courtesy gate, never the real
        // one (R3).
        if (
          !(POOL_BANNER_CONTENT_TYPES as readonly string[]).includes(file.type)
        ) {
          setState({ kind: "failed", message: null });
          return;
        }
        // An empty file and an oversized one are two different refusals. Only
        // the oversized one is a size problem, so only it gets the size
        // sentence (ISSUE-839).
        if (file.size > MAX_POOL_BANNER_BYTES) {
          setState({ kind: "failed", message: FILE_TOO_LARGE_COPY });
          return;
        }
        if (file.size < 1) {
          setState({ kind: "failed", message: null });
          return;
        }

        // 3. Mint the request id once and reuse it for the rest of this
        // call. The preview URL is created once per upload, after the local
        // pre-checks pass.
        isUploadingRef.current = true;
        setState({ kind: "uploading" });
        const requestId = crypto.randomUUID();
        const previewUrl = URL.createObjectURL(file);

        // 4. /upload/init
        let init;
        try {
          init = await uploadInit.mutateAsync({
            purpose: "poolBanner",
            contentType: file.type as typePoolBannerContentType,
            byteSize: file.size,
            fileName: file.name,
          });
        } catch {
          isUploadingRef.current = false;
          setState({ kind: "failed", message: null });
          return;
        }
        if (init.error != null || init.data == null) {
          isUploadingRef.current = false;
          setState({
            kind: "failed",
            message: photoUploadRefusalMessage(init),
          });
          return;
        }

        // 5. The presigned PUT. The browser does this itself with a bare
        // fetch, never through any SDK: the URL is presigned and points at
        // object storage, not at a sportsmet service, so it carries no
        // session header and no browser-managed session cookies. The whole
        // File is the body; no FormData, no multipart, no chunking.
        let putResponse: Response;
        try {
          putResponse = await fetch(init.data.uploadUrl, {
            method: init.data.uploadMethod,
            headers: { "content-type": init.data.uploadHeaders.contentType },
            body: file,
          });
        } catch {
          isUploadingRef.current = false;
          setState({ kind: "failed", message: null });
          return;
        }
        if (!putResponse.ok) {
          isUploadingRef.current = false;
          setState({ kind: "failed", message: null });
          return;
        }

        // 6. /upload/complete. Its status has exactly three possible values.
        let complete;
        try {
          complete = await uploadComplete.mutateAsync({
            mediaId: init.data.mediaId,
            requestId,
          });
        } catch {
          isUploadingRef.current = false;
          setState({ kind: "failed", message: null });
          return;
        }
        if (complete.error != null || complete.data == null) {
          isUploadingRef.current = false;
          setState({
            kind: "failed",
            message: photoUploadRefusalMessage(complete),
          });
          return;
        }
        if (
          complete.data.status === "pendingReview" ||
          complete.data.status === "heldOnBudget"
        ) {
          // The rolling-deploy fallback from the header. Not a failure, not
          // a rejection, and never the failed copy.
          isUploadingRef.current = false;
          setState({ kind: "pendingReview", previewUrl });
          return;
        }

        isUploadingRef.current = false;
        setState({
          kind: "ready",
          mediaId: complete.data.mediaId,
          previewUrl,
        });
      }

      void run();
    },
    [uploadInit, uploadComplete],
  );

  const reset = useCallback(() => {
    setState((current) => {
      if (current.kind === "ready" || current.kind === "pendingReview") {
        URL.revokeObjectURL(current.previewUrl);
      }
      return { kind: "idle" };
    });
  }, []);

  return { state, uploadBanner, reset };
}
